import { useCallback, useEffect, useState } from "react"

// Global, per-device preference for the desktop sidebar. Modeled on
// use-tasks-view-mode.ts (lazy init + write-through + cross-tab sync),
// reduced to a boolean. Default expanded — the labeled menu is the
// least-surprising first-visit state (matches the Figma default).
const STORAGE_KEY = "zplanem:sidebarExpanded"
const DEFAULT_EXPANDED = true

function decode(value: string | null): boolean {
	if (value === "1") return true
	if (value === "0") return false
	return DEFAULT_EXPANDED
}

function readStored(): boolean {
	if (typeof window === "undefined") return DEFAULT_EXPANDED
	try {
		return decode(window.localStorage.getItem(STORAGE_KEY))
	} catch {
		return DEFAULT_EXPANDED
	}
}

function writeStored(expanded: boolean) {
	if (typeof window === "undefined") return
	try {
		window.localStorage.setItem(STORAGE_KEY, expanded ? "1" : "0")
	} catch {
		// localStorage unavailable (private mode, quota) — silently drop
	}
}

export function useSidebarExpanded() {
	const [expanded, setExpandedState] = useState<boolean>(() => readStored())

	const setExpanded = useCallback((next: boolean) => {
		writeStored(next)
		setExpandedState(next)
	}, [])

	useEffect(() => {
		if (typeof window === "undefined") return
		const onStorage = (event: StorageEvent) => {
			if (event.key === STORAGE_KEY) {
				setExpandedState(decode(event.newValue))
			}
		}
		window.addEventListener("storage", onStorage)
		return () => window.removeEventListener("storage", onStorage)
	}, [])

	return { expanded, setExpanded }
}
